import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";

import useInitialChatModeFromMessages from "./hooks/useInitialChatModeFromMessages";
import usePlaygroundModeFromUrl from "./hooks/usePlaygroundModeFromUrl";
import usePlaygroundSession from "./hooks/usePlaygroundSession";
import { usePlayground } from "./playground-store";
import PlaygroundCore from "./PlaygroundCore";
import { PlaygroundDialogStoreProvider } from "./PlaygroundDialog/playground-dialog-store";
import PlaygroundShimmer from "./PlaygroundShimmer";

import { IS_RECAPTCHA_ENABLED, IS_RECAPTCHA_ENABLED_PLAYGROUND } from "@/constants";
import { GoogleReCaptchaProvider } from "@/providers/GoogleReCaptchaProvider";
import type { PromptBlueprint, PromptVersion } from "@/types";
import type { InputVariableRow } from "@/types/playground";
import type { VariableSet } from "@/components/Playground/types";
import type { PlaygroundSessionSource } from "@/queries/playground/useUpdatePlaygroundSession";

interface PlaygroundWithSessionProps {
  blueprint?: Partial<PromptBlueprint>;
  inputVariables?: InputVariableRow[];
  inputVariableSets?: VariableSet[];
  selectedTemplateVersion?: PromptVersion;
  readonly?: boolean;
  editor?: boolean;
  isSnippet?: boolean;
  instanceId?: string;
  request_log_ids?: number[];
  snippet_overrides?: Record<string, string>;
  source?: PlaygroundSessionSource;
  editorActions?: ReactNode;
}

const PlaygroundWithSession = ({ editorActions }: PlaygroundWithSessionProps) => {
  const { sessionId } = useParams();
  const playground = usePlayground();
  usePlaygroundSession();
  useInitialChatModeFromMessages(); // Auto-switch to chat mode if chat messages exist
  usePlaygroundModeFromUrl();

  return playground.isPendingSessionInitialization || !sessionId ? (
    <PlaygroundShimmer isChatMode={playground.isSessionModeChat} />
  ) : (
    <GoogleReCaptchaProvider
      showDisclaimer={false}
      enabled={IS_RECAPTCHA_ENABLED && IS_RECAPTCHA_ENABLED_PLAYGROUND}
    >
      <PlaygroundDialogStoreProvider>
        <PlaygroundCore editorActions={editorActions} />
      </PlaygroundDialogStoreProvider>
    </GoogleReCaptchaProvider>
  );
};

export default observer(PlaygroundWithSession);
